import type { AxiosResponse } from 'axios';
import { httpClientMain } from './httpClients';
import {
  Platform,
  FetchersResult,
  AddressSystemType,
  NSOwner,
  AirdropFetchersResult,
} from '@sonarwatch/portfolio-core';
import { Points, PointsPlatform, ValidatorInfo } from '@/types';
import { useAppStore } from '@/store/app';
import { validatorAccount } from '@/constants';

export async function verifyTurnstile() {
  const store = useAppStore();
  const res: AxiosResponse<string> = await httpClientMain.get(
    '/verify-turnstile',
    {
      headers: {
        'X-TURNSTILE-TOKEN': store.turnstileToken,
      },
    },
  );
  const { data } = res;
  return typeof data === 'string';
}

export async function getPlatforms() {
  const res: AxiosResponse<Platform[]> = await httpClientMain.get(
    '/portfolio/platforms',
  );
  const { data } = res;
  return data;
}

export async function getPointsPlatforms() {
  const res: AxiosResponse<PointsPlatform[]> =
    await httpClientMain.get('/points/platforms');
  const { data } = res;
  return data;
}

export async function getPoints(address: string): Promise<Points[] | null> {
  const store = useAppStore();
  const res: AxiosResponse<Points[]> | null = await httpClientMain
    .get(`/points/getPoints/${address}`, {
      headers: {
        'X-TURNSTILE-TOKEN': store.turnstileToken,
      },
    })
    .catch(() => null);
  if (!res) return null;
  return res.data;
}

export async function getOwner(name: string): Promise<NSOwner | null> {
  const store = useAppStore();
  const res: AxiosResponse<NSOwner> | null = await httpClientMain
    .get(`/names/getOwner/${name}`, {
      headers: {
        'X-TURNSTILE-TOKEN': store.turnstileToken,
      },
    })
    .catch(() => null);
  if (!res) return null;
  return res.data;
}

export async function getPrices(): Promise<any | null> {
  const store = useAppStore();
  const res: AxiosResponse<any> | null = await httpClientMain
    .get(`/prices`, {
      headers: {
        'X-TURNSTILE-TOKEN': store.turnstileToken,
      },
    })
    .catch(() => null);
  if (!res) return null;
  return res.data;
}

export async function getAddressFromName(
  address: string,
): Promise<string | null> {
  const store = useAppStore();
  const res: AxiosResponse<string[]> | null = await httpClientMain
    .get(`/names/getNames/${address}`, {
      headers: {
        'X-TURNSTILE-TOKEN': store.turnstileToken,
      },
    })
    .catch(() => null);
  if (!res) return null;
  if (res.data[0] == null) return null;
  return res.data[0];
}

export async function portfolioFetchFromName(name: string) {
  const owner = await getOwner(name);
  if (!owner) throw new Error(`No owner: ${name}`);
  return portfolioFetch(owner.address, owner.addressSystem);
}

export async function portfolioFetch(
  address: string,
  addressSystem: AddressSystemType,
) {
  const store = useAppStore();
  const res: AxiosResponse<FetchersResult> = await httpClientMain.get(
    `/portfolio/fetch`,
    {
      params: {
        address,
        addressSystem,
      },
      headers: {
        'X-TURNSTILE-TOKEN': store.turnstileToken,
      },
    },
  );
  const { data } = res;
  return {
    ...data,
    elements: data.elements.map((e) => ({ ...e, owner: address })),
  };
}

export async function airdropsFetch(
  address: string,
  addressSystem: AddressSystemType,
) {
  const store = useAppStore();
  const res: AxiosResponse<AirdropFetchersResult> = await httpClientMain.get(
    `/airdrops/fetch`,
    {
      params: {
        address,
        addressSystem,
      },
      headers: {
        'X-TURNSTILE-TOKEN': store.turnstileToken,
      },
    },
  );
  const { data } = res;
  return data;
}

export async function getValidator() {
  const res: AxiosResponse<ValidatorInfo> = await httpClientMain.get(
    `/validators/solana/${validatorAccount}`,
  );
  const { data } = res;
  return data;
}

export async function stakingFetch(
  address: string,
  addressSystem: AddressSystemType,
) {
  const store = useAppStore();
  const res: AxiosResponse<FetchersResult> = await httpClientMain.get(
    `/portfolio/fetchStakingOnly`,
    {
      params: {
        address,
        addressSystem,
      },
      headers: {
        'X-TURNSTILE-TOKEN': store.turnstileToken,
      },
    },
  );
  const { data } = res;
  return {
    ...data,
    elements: data.elements.map((e) => ({ ...e, owner: address })),
  };
}
